import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  Code2,
  Smartphone,
  Globe2,
  Palette,
  UsersRound,
  Network,
} from "lucide-react";

// ============================================================
// ICON MAP
// ============================================================

const iconMap = {
  code: Code2,
  mobile: Smartphone,
  web: Globe2,
  design: Palette,
  crm: UsersRound,
  mlm: Network,
};

// ============================================================
// ANIMATION
// ============================================================

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 30,
  },

  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

// ============================================================
// SERVICE CARD
// ============================================================

const ServiceCard = ({ service, index = 0 }) => {
  const navigate = useNavigate();

  const Icon = iconMap[service?.icon] || Code2;

  // ============================================================
  // HANDLE CARD CLICK
  // ============================================================

  const handleClick = () => {
    if (!service?.href) return;

    navigate(service.href);

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <motion.article
      variants={cardVariants}
      custom={index}
      initial="hidden"
      whileInView="visible"
      viewport={{
        once: true,
        amount: 0.12,
      }}
      whileHover={{
        y: -6,
      }}
      transition={{
        duration: 0.3,
        ease: "easeOut",
      }}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleClick();
        }
      }}
      className="
        group
        relative
        flex
        h-full
        min-h-[245px]
        w-full
        min-w-0
        cursor-pointer
        flex-col
        overflow-hidden
        rounded-2xl
        border
        border-[var(--border-primary)]
        bg-[var(--card-bg)]
        transition-all
        duration-300

        hover:border-orange-500/40
        hover:shadow-[0_20px_60px_var(--shadow-color)]

        min-[375px]:min-h-[255px]

        sm:min-h-[285px]
        sm:rounded-3xl

        md:min-h-[325px]
      "
    >
      {/* ======================================================
          BACKGROUND GLOW
      ======================================================= */}

      <div
        className="
          pointer-events-none
          absolute
          -right-16
          -top-16
          h-28
          w-28
          rounded-full
          bg-orange-500/10
          opacity-0
          blur-3xl
          transition-opacity
          duration-500
          group-hover:opacity-100

          min-[375px]:-right-20
          min-[375px]:-top-20
          min-[375px]:h-36
          min-[375px]:w-36

          sm:h-48
          sm:w-48
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -bottom-16
          -left-16
          h-32
          w-32
          rounded-full
          bg-orange-500/5
          opacity-0
          blur-3xl
          transition-opacity
          duration-500
          group-hover:opacity-100

          min-[375px]:-bottom-24
          min-[375px]:-left-20
          min-[375px]:h-40
          min-[375px]:w-40

          sm:h-44
          sm:w-44
        "
      />

      {/* ======================================================
          TOP ACCENT LINE
      ======================================================= */}

      <div
        className="
          pointer-events-none
          absolute
          left-1/2
          top-0
          h-[2px]
          w-0
          -translate-x-1/2
          bg-gradient-to-r
          from-transparent
          via-orange-500
          to-transparent
          transition-all
          duration-500
          group-hover:w-[60%]
        "
      />

      {/* ======================================================
          CARD CONTENT
      ======================================================= */}

      <div
        className="
          relative
          z-10
          flex
          h-full
          min-w-0
          flex-col

          p-3

          min-[375px]:p-3.5

          sm:p-5

          md:p-6
        "
      >
        {/* ==================================================
            TOP ROW
        =================================================== */}

        <div
          className="
            mb-2.5
            flex
            min-h-8
            items-center
            justify-end

            min-[375px]:mb-3

            sm:mb-5
            sm:min-h-9
          "
        >
          {/* Arrow */}

          <div
            className="
              flex
              h-7
              w-7
              shrink-0
              items-center
              justify-center
              rounded-full
              border
              border-[var(--border-primary)]
              bg-[var(--bg-tertiary)]
              text-[var(--text-muted)]
              transition-all
              duration-300

              group-hover:border-orange-500/40
              group-hover:bg-orange-500
              group-hover:text-white

              min-[375px]:h-8
              min-[375px]:w-8

              sm:h-9
              sm:w-9
            "
          >
            <ArrowUpRight
              size={14}
              className="
                transition-transform
                duration-300

                group-hover:translate-x-0.5
                group-hover:-translate-y-0.5

                min-[375px]:h-[15px]
                min-[375px]:w-[15px]
              "
            />
          </div>
        </div>

        {/* ==================================================
            ICON
        =================================================== */}

        <div
          className="
            mb-2.5
            flex
            h-9
            w-9
            shrink-0
            items-center
            justify-center
            rounded-xl
            bg-orange-500/10
            text-orange-500
            transition-all
            duration-300

            group-hover:bg-orange-500
            group-hover:text-white

            min-[375px]:mb-3
            min-[375px]:h-10
            min-[375px]:w-10

            sm:mb-5
            sm:h-12
            sm:w-12
            sm:rounded-2xl

            md:h-14
            md:w-14
          "
        >
          <Icon
            size={18}
            strokeWidth={1.8}
            className="
              transition-transform
              duration-300
              group-hover:scale-110

              min-[375px]:h-5
              min-[375px]:w-5

              sm:h-6
              sm:w-6

              md:h-7
              md:w-7
            "
          />
        </div>

        {/* ==================================================
            TITLE
        =================================================== */}

        <h3
          className="
            max-w-full
            break-words

            text-base
            font-bold
            leading-[1.2]
            text-[var(--text-primary)]

            transition-colors
            duration-300

            group-hover:text-orange-400

            min-[375px]:text-[17px]

            sm:text-xl

            md:text-2xl
          "
        >
          {service?.title}
        </h3>

        {/* ==================================================
            DESCRIPTION
        =================================================== */}

        <p
          className="
            mt-1.5
            w-full
            min-w-0
            break-words
            line-clamp-3

            text-[11px]
            leading-[1.65]
            text-[var(--text-muted)]

            transition-colors
            duration-300

            group-hover:text-[var(--text-secondary)]

            min-[375px]:mt-2
            min-[375px]:text-[11px]

            sm:mt-3
            sm:text-sm
            sm:leading-6
          "
        >
          {service?.description}
        </p>

        {/* ==================================================
            BOTTOM CTA
        =================================================== */}

        <div
          className="
            mt-auto
            min-w-0
            pt-2.5

            min-[375px]:pt-3

            sm:pt-5
          "
        >
          <div
            className="
              inline-flex
              max-w-full
              min-w-0
              items-center
              gap-1.5

              text-[11px]
              font-semibold
              text-orange-400

              transition-all
              duration-300

              min-[375px]:gap-2
              min-[375px]:text-xs

              sm:gap-2.5
              sm:text-sm
            "
          >
            <span className="truncate">
              Explore Service
            </span>

            <ArrowUpRight
              size={14}
              className="
                shrink-0
                transition-transform
                duration-300

                group-hover:translate-x-1
                group-hover:-translate-y-0.5

                min-[375px]:h-[15px]
                min-[375px]:w-[15px]

                sm:h-4
                sm:w-4
              "
            />
          </div>
        </div>
      </div>

      {/* ======================================================
          CORNER DECORATION
      ======================================================= */}

      <div
        className="
          pointer-events-none
          absolute
          -bottom-8
          -right-8
          h-14
          w-14
          rounded-full
          border
          border-orange-500/[0.05]
          transition-transform
          duration-500
          group-hover:scale-125

          min-[375px]:-bottom-10
          min-[375px]:-right-10
          min-[375px]:h-16
          min-[375px]:w-16

          sm:h-24
          sm:w-24
        "
      />
    </motion.article>
  );
};

export default ServiceCard;